import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
} from "@mui/material";
import { translate } from "../../utils/localisation";

type Props = {
  open: boolean;
  onClose: () => void;
  detail: any | null;
  loading?: boolean;
};

function PrettyJson({ data }: { data: any }) {
  if (!data)
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("audit-logs.no-data") || "—"}
      </Typography>
    );
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1, bgcolor: "background.paper", overflow: "auto" }}
    >
      <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(data, null, 2)}
      </Box>
    </Paper>
  );
}

export default function AuditDetailModal({ open, onClose, detail }: Props) {
  const created = detail?.createdAt
    ? new Date(detail.createdAt).toLocaleString()
    : detail?.timestamp
      ? new Date(detail.timestamp).toLocaleString()
      : "—";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {translate("audit-logs.detail-title") || "Audit Entry"}
      </DialogTitle>
      <DialogContent dividers>
        {!detail && (
          <Typography variant="body2">
            {translate("audit-logs.loading") || "Loading..."}
          </Typography>
        )}

        {detail && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">
                {translate("audit-logs.user") || "User"}
              </Typography>
              <Typography variant="body1">{detail.userEmail || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">
                {translate("audit-logs.created") || "Created"}
              </Typography>
              <Typography variant="body1">{created}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">
                {translate("audit-logs.type") || "Type"}
              </Typography>
              <Typography variant="body1">{detail.type || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">
                {translate("audit-logs.target") || "Target"}
              </Typography>
              <Typography variant="body1">
                {detail.targetParameter || "—"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                {translate("audit-logs.comment") || "Comment"}
              </Typography>
              <Typography variant="body1">{detail.comment || "—"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                {translate("audit-logs.patch") || "Patch"}
              </Typography>
              <PrettyJson data={detail.patch} />
            </Grid>

            {detail.requestSnapshot && (
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  {translate("audit-logs.request-snapshot") ||
                    "Request Snapshot"}
                </Typography>
                {detail.requestSnapshot.body ? (
                  <PrettyJson data={detail.requestSnapshot.body} />
                ) : (
                  <PrettyJson data={detail.requestSnapshot} />
                )}
              </Grid>
            )}

            {!detail.requestSnapshot && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {translate("audit-logs.no-request-snapshot") ||
                    "No request snapshot was provided by the backend for this entry."}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">
                {translate("audit-logs.raw-entry") || "Raw entry"}
              </Typography>
              <PrettyJson data={detail} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {translate("audit-logs.close") || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
